import { subclass } from "@arcgis/core/core/accessorSupport/decorators";
import LocateViewModel from "@arcgis/core/widgets/Locate/LocateViewModel";
import Error from "@arcgis/core/core/Error";
import * as projection from "@arcgis/core/geometry/projection";
import Point from "@arcgis/core/geometry/Point";

const _LocallyProjectingLocateViewModel = class LocallyProjectingLocateViewModel extends LocateViewModel {
  /**
   * @param {Position} position
   * @param {{
    signal: AbortSignal;
  }} options
 */
  async _setPosition(position, options) {
    // This method reimplements the base class' implementation to avoid the need
    // to project geolocation coordinates via ESRI's server side
    try {
      if (!projection.isLoaded()) {
        await projection.load();
      }
      const {
        latitude,
        longitude,
        accuracy,
        altitude,
        altitudeAccuracy,
        heading,
        speed,
      } = position.coords;

      // Project from GPS coords (WGS84/EPSG:4326) to the map's view (probably British National Grid (EPSG:27700))
      const point = projection.project(
        new Point({
          latitude,
          longitude,
          spatialReference: { wkid: 4326 },
        }),
        this.view.spatialReference
      );

      if (this.graphic) {
        this.graphic.geometry = point;
        this.graphic.attributes = {
          latitude,
          longitude,
          accuracy,
          altitude,
          altitudeAccuracy,
          heading,
          speed,
          timestamp: position.timestamp,
        };
      }

      const scale = this._getScale(this.view);
      return this._animatePoint(point, position, scale, options);
    } catch (e) {
      throw new Error(
        "positioning:invalid-point",
        "Cannot position invalid point",
        {
          error: e,
        }
      );
    }
  }
};

// For documentation around this, check out
// https://developers.arcgis.com/javascript/latest/implementing-accessor/#create-a-simple-subclass
// Instead of using
//   @decorator(args) class ClassName {}
//, we use
//   const ClassName = decorator(args)(class ClassName {})
// as CRA doesn't support decorators yet and the ESRI createSubclass method
// loses all typedefs. To restore our typedef here, use a JSDoc @type with typeof.
/**
 * @type {typeof _LocallyProjectingLocateViewModel}
 */
export const LocallyProjectingLocateViewModel = subclass(
  "app.widgets.Locate.FixedLocateViewModel"
)(_LocallyProjectingLocateViewModel);
